import { memo, useCallback } from 'react'

import { Cancel } from '@mui/icons-material'
import { Box, TextField } from '@mui/material'
import { OriginGateway, OriginGatewayInsert } from '@nativewaves/nw-cloud-sdk/origin'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useShallow } from 'zustand/react/shallow'

import { useCloseHandler } from '@shared/components/MaterialUIEnhancements/Dialog'
import {
  useSubmissionProgressStore,
  selectSubmissionProgressSetters,
  SubmissionProgress,
  SubmissionButton,
} from '@shared/components/SubmissionProgress'
import { DialogFormContent } from '@shared/layouts'
import { useGatewayCreateMutation } from 'hooks/mutations/origin'
import { originGatewaysOverviewRoute } from 'pages/NWPlatform/OriginGateways'

type FormStructure = OriginGatewayInsert

type NewDialogProps = {
  onSuccess?: (data: OriginGateway) => void
  initialValue?: OriginGateway
}

const NewDialogRaw = ({ onSuccess, initialValue }: NewDialogProps) => {
  const { t } = useTranslation(['common', 'domain'])
  const handleClose = useCloseHandler()

  const { environmentId } = originGatewaysOverviewRoute.useParams()

  const { setCurrentStep } = useSubmissionProgressStore(useShallow(selectSubmissionProgressSetters))

  const { mutate: createGateway, ...gatewayCreateProps } = useGatewayCreateMutation({})

  const { handleSubmit, control } = useForm<FormStructure>({
    defaultValues: {
      environmentId,
      ...initialValue,
    },
  })

  const handleFormSubmit = useCallback(
    (data: FormStructure) => {
      setCurrentStep({ value: 10, description: t('domain:Workspace.Gateway.creatingGateway') })

      createGateway(
        { originGatewayInsert: data },
        {
          onSuccess: (data) => {
            setTimeout(() => {
              handleClose()

              if (onSuccess) {
                setTimeout(() => {
                  onSuccess(data)
                }, 300)
              }
            }, 700)
          },
          onError: () => {
            setCurrentStep({
              value: 100,
              description: t('somethingWentWrong'),
              icon: <Cancel fontSize="small" color="error" />,
            })
          },
        },
      )
    },
    [setCurrentStep, t, createGateway, handleClose, onSuccess],
  )

  return (
    <DialogFormContent
      onSubmit={handleSubmit(handleFormSubmit)}
      title={t('domain:Workspace.Gateway.addGateway')}
      SubmitProgressZone={
        <SubmissionProgress isSuccess={gatewayCreateProps.isSuccess} isError={gatewayCreateProps.isError} />
      }
      SaveButton={
        <SubmissionButton
          submitCondition={!gatewayCreateProps.isSuccess && !gatewayCreateProps.isError}
          successCondition={gatewayCreateProps.isSuccess}
          errorCondition={gatewayCreateProps.isError}
          loading={gatewayCreateProps.isPending}
          disabled={gatewayCreateProps.isPending || gatewayCreateProps.isSuccess}
        />
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: 2, my: 2 }}>
        <Controller
          control={control}
          name="name"
          rules={{
            required: true,
          }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label={t('name')}
              required
              helperText={fieldState.error?.message}
              error={!!fieldState.error}
            />
          )}
        />
      </Box>
    </DialogFormContent>
  )
}

export const NewDialog = memo(NewDialogRaw)
